'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from './ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: string;
  badge?: number;
  group?: string;
}

const memberNavItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/member-dashboard', icon: 'HomeIcon', group: 'main' },
  { id: 'nav-store', label: 'My Store', href: '/member-personal-store', icon: 'ShoppingBagIcon', group: 'main' },
  { id: 'nav-products', label: 'Products', href: '/product-catalog', icon: 'CubeIcon', group: 'main' },
  { id: 'nav-orders', label: 'Orders', href: '/member-dashboard', icon: 'ClipboardDocumentListIcon', badge: 3, group: 'business' },
  { id: 'nav-customers', label: 'Customers', href: '/member-dashboard', icon: 'UsersIcon', group: 'business' },
  { id: 'nav-team', label: 'My Team', href: '/my-team', icon: 'UserGroupIcon', group: 'business' },
  { id: 'nav-earnings', label: 'Earnings', href: '/member-dashboard', icon: 'BanknotesIcon', group: 'business' },
  { id: 'nav-academy', label: 'Academy', href: '/member-dashboard', icon: 'AcademicCapIcon', badge: 2, group: 'growth' },
  { id: 'nav-marketing', label: 'Marketing', href: '/member-dashboard', icon: 'MegaphoneIcon', group: 'growth' },
  { id: 'nav-settings', label: 'Settings', href: '/member-dashboard', icon: 'Cog6ToothIcon', group: 'account' },
];

const groups = [
  { id: 'main', label: 'Overview' },
  { id: 'business', label: 'Business' },
  { id: 'growth', label: 'Growth' },
  { id: 'account', label: 'Account' },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
  variant?: 'member' | 'admin';
  userDisplayName?: string;
  userInitials?: string;
  userPackage?: string;
}

export default function DashboardLayout({ children, variant = 'member', userDisplayName, userInitials, userPackage }: DashboardLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();
  const { theme, toggleTheme } = useTheme();
  const { signOut, user } = useAuth();
  const router = useRouter();

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const displayName = userDisplayName || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Member';
  const initials = userInitials || displayName.split(' ').filter(Boolean).slice(0, 2).map((w: string) => w[0]?.toUpperCase()).join('') || 'M';
  const packageLabel = userPackage || 'Member';

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`flex-shrink-0 flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out ${
          collapsed ? 'w-16' : 'w-60'
        } hidden md:flex`}
      >
        {/* Logo */}
        <div className={`flex items-center h-16 border-b border-border px-4 ${collapsed ? 'justify-center' : 'gap-2.5'}`}>
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-display text-base font-semibold tracking-wide text-foreground truncate">
              RICHÉSSE
            </span>
          )}
        </div>

        {/* Member Info */}
        {!collapsed && (
          <div className="px-4 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-gold flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-white">{initials}</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                <p className="text-xs text-primary font-medium">{packageLabel}</p>
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 scrollbar-hide">
          {groups.map(group => {
            const items = memberNavItems.filter(i => i.group === group.id);
            if (!items.length) return null;
            return (
              <div key={`group-${group.id}`} className="mb-2">
                {!collapsed && (
                  <p className="px-4 py-1.5 text-[10px] font-semibold tracking-widest uppercase text-muted-foreground/60">
                    {group.label}
                  </p>
                )}
                {items.map(item => {
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={item.id}
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      className={`flex items-center mx-2 rounded-lg mb-0.5 transition-all duration-150 ${
                        collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
                      } ${isActive ? 'sidebar-link-active' : 'sidebar-link'}`}
                    >
                      <Icon name={item.icon as any} size={18} />
                      {!collapsed && (
                        <>
                          <span className="text-sm font-medium flex-1">{item.label}</span>
                          {item.badge && (
                            <span className="bg-primary/20 text-primary text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="border-t border-border p-3 space-y-1">
          <button
            onClick={toggleTheme}
            className={`sidebar-link w-full rounded-lg flex items-center transition-all duration-150 ${
              collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
            }`}
            aria-label="Toggle theme"
          >
            <Icon name={theme === 'dark' ? 'SunIcon' : 'MoonIcon'} size={18} />
            {!collapsed && <span className="text-sm font-medium">Toggle Theme</span>}
          </button>
          <button
            onClick={handleSignOut}
            className={`sidebar-link w-full rounded-lg flex items-center transition-all duration-150 ${
              collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
            }`}
            aria-label="Sign out"
          >
            <Icon name="ArrowRightOnRectangleIcon" size={18} />
            {!collapsed && <span className="text-sm font-medium">Sign Out</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`sidebar-link w-full rounded-lg flex items-center transition-all duration-150 ${
              collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
            }`}
            aria-label="Collapse sidebar"
          >
            <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={18} />
            {!collapsed && <span className="text-sm font-medium">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="md:hidden">
              <AppLogo size={28} />
            </div>
            <div className="relative hidden sm:block">
              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search..."
                className="richesse-input pl-9 py-2 text-sm w-56 lg:w-72"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/member-personal-store" className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-dark transition-colors px-3 py-1.5 rounded-lg border border-primary/30 hover:bg-primary/5">
              <Icon name="ArrowTopRightOnSquareIcon" size={14} />
              My Store
            </Link>
            <button className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors" aria-label="Notifications">
              <Icon name="BellIcon" size={18} className="text-muted-foreground" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full border-2 border-card" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-gold flex items-center justify-center cursor-pointer" title={displayName}>
              <span className="text-xs font-bold text-white">{initials}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden flex items-center justify-around border-t border-border bg-card h-16 flex-shrink-0">
          {[
            { id: 'mob-home', label: 'Home', icon: 'HomeIcon', href: '/member-dashboard' },
            { id: 'mob-store', label: 'Store', icon: 'ShoppingBagIcon', href: '/member-personal-store' },
            { id: 'mob-orders', label: 'Orders', icon: 'ClipboardDocumentListIcon', href: '/member-dashboard' },
            { id: 'mob-team', label: 'Team', icon: 'UserGroupIcon', href: '/my-team' },
            { id: 'mob-more', label: 'More', icon: 'EllipsisHorizontalIcon', href: '/member-dashboard' },
          ].map(item => (
            <Link key={item.id} href={item.href} className="flex flex-col items-center gap-1 py-2 px-3 text-muted-foreground hover:text-primary transition-colors">
              <Icon name={item.icon as any} size={20} />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
      </div>
    </div>
  );
}